import React from 'react'
import { Link } from 'react-router-dom'
import ContactForm from './ContactForm'
import SEO from './SEO'
import { trackCtaClick } from '../utils/analytics'
import './../styles/Marketing.css'

const services = [
  {
    icon: 'web',
    title: 'Website Design & Build',
    body:
      'Modern, responsive websites built for speed, conversion and search visibility — from landing pages to full multi-page sites.',
    points: ['Custom design', 'Responsive on every device', 'Optimised for Core Web Vitals', 'CMS or static — your choice'],
  },
  {
    icon: 'seo',
    title: 'SEO',
    body:
      'On-page, technical and local SEO that helps the right customers find you on Google. Built-in audits, keyword strategy and ongoing improvement.',
    points: ['Technical SEO audit', 'Keyword research & content plan', 'On-page optimisation', 'Local & Maps SEO'],
  },
  {
    icon: 'analytics',
    google: true,
    title: 'Google Analytics 4',
    body:
      'Proper GA4 setup so you actually understand what your visitors do — not just vanity metrics. Custom dashboards and reports included.',
    points: ['GA4 property & data streams', 'Cross-domain tracking', 'Custom dashboards', 'Privacy-compliant config'],
  },
  {
    icon: 'events',
    google: true,
    title: 'Event Tracking',
    body:
      'Track the actions that matter — clicks, form submits, scrolls, video plays, purchases. Wired through Google Tag Manager, clean and maintainable.',
    points: ['Google Tag Manager setup', 'Custom events & conversions', 'eCommerce tracking', 'Funnel measurement'],
  },
  {
    icon: 'search',
    google: true,
    title: 'Search Console',
    body:
      'Get a direct line to Google. We configure Search Console, submit sitemaps, monitor coverage and fix the issues stopping you from ranking.',
    points: ['Verification & sitemap', 'Index coverage fixes', 'Rich-result monitoring', 'Performance reporting'],
  },
  {
    icon: 'profile',
    google: true,
    title: 'Google Business Profile & Knowledge Panel',
    body:
      'Own your search result. We claim, verify and optimise your Google Business Profile and Knowledge Panel so customers see you at your best.',
    points: ['Profile claim & verification', 'Photos, hours, services', 'Review strategy', 'Knowledge Panel optimisation'],
  },
  {
    icon: 'ads',
    google: true,
    title: 'Google Ads',
    body:
      'Performance-driven Google Ads — Search, Performance Max and remarketing. Built to bring qualified leads, not just clicks.',
    points: ['Account setup & structure', 'Keyword & audience strategy', 'Conversion tracking', 'Ongoing optimisation'],
  },
  {
    icon: 'social',
    title: 'Social Media',
    to: '/marketing/social-media',
    body:
      'Strategy, content creation, photography & video, day-to-day management and paid social — all on-brand, all on cadence.',
    points: ['Strategy & content pillars', 'Photo & video production', 'Community management', 'Meta, TikTok & LinkedIn ads'],
  },
  {
    icon: 'rebrand',
    title: 'Full Rebrand & Makeover',
    body:
      'A complete brand makeover — new logo, identity, voice and visuals — paired with a fresh website that takes your business into its next chapter.',
    points: ['Logo & visual identity', 'Brand guidelines', 'Tone of voice', 'Website refresh'],
  },
]

/* Bullet icons used in the "Why we're different" section. Stroke-only
   line icons, sized to sit next to body copy. */
const BulletIcon = ({ name }) => {
  const common = {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    className: 'mkt-bullet-icon',
  }
  switch (name) {
    case 'plug': // electrical plug — plugin bloat
      return (
        <svg {...common}>
          <path d="M9 2v4M15 2v4" />
          <rect x="7" y="6" width="10" height="6" rx="2" />
          <path d="M12 12v4a4 4 0 0 0 4 4h0" />
        </svg>
      )
    case 'lock': // limited customisation
      return (
        <svg {...common}>
          <rect x="4" y="11" width="16" height="9" rx="2" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
      )
    case 'speed': // performance / speedometer
      return (
        <svg {...common}>
          <path d="M12 14L18 8" />
          <path d="M3 14a9 9 0 0 1 18 0" />
          <circle cx="12" cy="14" r="1.4" fill="currentColor" />
        </svg>
      )
    case 'puzzle': // hard to manage / fragmentation
      return (
        <svg {...common}>
          <path d="M9 4h3a2 2 0 0 1 2 2v3h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2v-3h-3v3a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-3H4v-3a2 2 0 0 1 2-2h3V6a2 2 0 0 1 2-2z" />
        </svg>
      )
    case 'cpu': // aging tech stack — chip
      return (
        <svg {...common}>
          <rect x="5" y="5" width="14" height="14" rx="2" />
          <rect x="9" y="9" width="6" height="6" />
          <path d="M9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3" />
        </svg>
      )
    case 'shield': // security target — shield with alert
      return (
        <svg {...common}>
          <path d="M12 3l8 3v6a9 9 0 0 1-8 9 9 9 0 0 1-8-9V6l8-3z" />
          <path d="M12 9v4" />
          <circle cx="12" cy="16" r="0.6" fill="currentColor" stroke="none" />
        </svg>
      )
    case 'brush': // themes — paintbrush
      return (
        <svg {...common}>
          <path d="M14 3l7 7-9 9-3 1 1-3 4-4" />
          <path d="M5 19l-2 2" />
          <path d="M9 13l4 4" />
        </svg>
      )
    case 'template': // template grid
      return (
        <svg {...common}>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
      )
    case 'brand': // sparkles for branding
      return (
        <svg {...common}>
          <path d="M12 3l1.8 4.2L18 9l-4.2 1.8L12 15l-1.8-4.2L6 9l4.2-1.8L12 3z" />
          <path d="M19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9L19 15z" />
        </svg>
      )
    case 'box': // limited capability
      return (
        <svg {...common}>
          <path d="M3 8l9-5 9 5v8l-9 5-9-5V8z" />
          <path d="M3 8l9 5 9-5" />
          <path d="M12 13v8" />
        </svg>
      )
    case 'chain': // vendor lock-in
      return (
        <svg {...common}>
          <path d="M10 14a4 4 0 0 1 0-5.7l3-3a4 4 0 0 1 5.7 5.7l-1.5 1.5" />
          <path d="M14 10a4 4 0 0 1 0 5.7l-3 3a4 4 0 0 1-5.7-5.7l1.5-1.5" />
        </svg>
      )
    default:
      return null
  }
}

/* Google "G" mark — official four colours, used to indicate that we
   set up and operate these Google products on behalf of clients. */
const GoogleG = ({ className = 'mkt-g' }) => (
  <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" className={className} aria-label="Google" role="img">
    <path fill="#4285F4" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
    <path fill="#34A853" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
    <path fill="#FBBC05" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
    <path fill="#EA4335" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
  </svg>
)

const reasons = [
  {
    title: 'We help businesses grow',
    body: 'Every decision is tied to outcomes that move your business forward — leads, revenue, visibility.',
  },
  {
    title: 'Built by engineers',
    body: 'We come from a software quality background. Your stack, tracking and site will actually work — and keep working.',
  },
  {
    title: 'Transparent reporting',
    body: 'You always know what we did, what it cost, and what it earned. Clear dashboards, no jargon.',
  },
  {
    title: 'One partner, joined-up',
    body: 'Website, SEO, Analytics, Ads, brand — all under one roof, working together instead of in silos.',
  },
]

const Marketing = () => (
  <>
    <SEO
      path="/marketing"
      title="Marketing Services — Web, SEO & Google Setup"
      description="Marketing services from QA Tech: website creation, SEO, Google Analytics & event tracking, Search Console, Google Business Profile, Google Ads and full company rebrands. We help businesses grow."
      keywords="marketing services, website creation, SEO, Google Analytics, GA4, Google Tag Manager, Search Console, Google Business Profile, Knowledge Panel, Google Ads, rebrand, branding, growth marketing, UK marketing agency"
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        name: 'QA Tech — Marketing Services',
        url: 'https://qatechltd.com/marketing',
        description:
          'Website creation, SEO, Google Analytics, Tag Manager, Search Console, Google Business Profile, Google Ads and full company rebrands.',
        provider: {
          '@type': 'Organization',
          name: 'QA Tech',
          url: 'https://qatechltd.com',
        },
        areaServed: 'Worldwide',
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Marketing services',
          itemListElement: [
            'Website Design & Build',
            'SEO',
            'Google Analytics 4',
            'Event Tracking',
            'Search Console',
            'Google Business Profile & Knowledge Panel',
            'Google Ads',
            'Full Rebrand & Makeover',
          ].map((n) => ({
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: n },
          })),
        },
      }}
    />

    <div className="body marketing-body">
      {/* Hero */}
      <section className="mkt-hero">
        <div className="mkt-hero__content">
          <span className="eyebrow">Marketing &mdash; built to grow your business</span>
          <h1 className="mkt-hero__title">
            We want to help <span className="text-gradient">businesses grow</span>
          </h1>
          <p className="mkt-hero__sub">
            Websites, SEO, Google setup and full rebrands &mdash; the marketing
            foundations every modern business needs, delivered by a team that
            actually measures what matters.
          </p>
          <div className="mkt-hero__cta-row">
            <a
              href="#mkt-contact"
              className="get-in-touch-button"
              onClick={() => trackCtaClick('Start a project', 'marketing_hero')}
            >
              Start a project
            </a>
            <a
              href="#mkt-services"
              className="hero-secondary-link"
              onClick={() => trackCtaClick('See services', 'marketing_hero')}
            >
              See services &darr;
            </a>
          </div>
        </div>

        <div className="mkt-hero__visual" aria-hidden="true">
          <svg viewBox="0 0 420 420" xmlns="http://www.w3.org/2000/svg" className="mkt-hero__svg">
            <defs>
              <linearGradient id="mkt-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#0066ff" />
                <stop offset="100%" stopColor="#00d4ff" />
              </linearGradient>
              <radialGradient id="mkt-glow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.45" />
                <stop offset="100%" stopColor="#00d4ff" stopOpacity="0" />
              </radialGradient>
            </defs>

            <circle cx="210" cy="210" r="180" fill="url(#mkt-glow)" className="mkt-blob" />

            {/* growth line / chart */}
            <g className="mkt-chart">
              <polyline
                points="60,330 120,290 170,300 220,230 270,260 320,170 360,140"
                fill="none"
                stroke="url(#mkt-grad)"
                strokeWidth="5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="60"  cy="330" r="6" fill="#00d4ff" />
              <circle cx="120" cy="290" r="6" fill="#00d4ff" />
              <circle cx="170" cy="300" r="6" fill="#00d4ff" />
              <circle cx="220" cy="230" r="6" fill="#00d4ff" />
              <circle cx="270" cy="260" r="6" fill="#00d4ff" />
              <circle cx="320" cy="170" r="6" fill="#00d4ff" />
              <circle cx="360" cy="140" r="8" fill="#fff" stroke="#00d4ff" strokeWidth="3" className="mkt-chart__head" />
            </g>

            {/* arrow up */}
            <g className="mkt-arrow">
              <path
                d="M340 170 L380 130 L380 100 L350 100 Z"
                fill="url(#mkt-grad)"
                opacity="0.0"
              />
              <path
                d="M340 160 L380 120"
                stroke="url(#mkt-grad)"
                strokeWidth="5"
                strokeLinecap="round"
              />
              <path
                d="M380 120 L370 120 M380 120 L380 130"
                stroke="url(#mkt-grad)"
                strokeWidth="5"
                strokeLinecap="round"
              />
            </g>

            {/* floating circle badges */}
            <g className="mkt-badge mkt-badge--a" transform="translate(80 100)">
              <circle r="22" fill="rgba(255,255,255,0.06)" stroke="rgba(0,212,255,0.45)" strokeWidth="1.5" />
              <text x="0" y="5" textAnchor="middle" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="14" fill="#00d4ff">SEO</text>
            </g>
            <g className="mkt-badge mkt-badge--b" transform="translate(340 320)">
              <circle r="22" fill="rgba(255,255,255,0.06)" stroke="rgba(0,102,255,0.45)" strokeWidth="1.5" />
              <text x="0" y="5" textAnchor="middle" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="12" fill="#0066ff">ADS</text>
            </g>
            <g className="mkt-badge mkt-badge--c" transform="translate(60 240)">
              <circle r="20" fill="rgba(255,255,255,0.06)" stroke="rgba(0,212,255,0.35)" strokeWidth="1.5" />
              <text x="0" y="5" textAnchor="middle" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="12" fill="#00d4ff">GA4</text>
            </g>
          </svg>
        </div>
      </section>

      {/* Why we're different — top of page differentiator */}
      <section className="mkt-difference" aria-labelledby="mkt-diff-h">
        <div className="mkt-difference__inner">
          <div className="mkt-difference__head">
            <span className="eyebrow">Why we're different</span>
            <h2 id="mkt-diff-h" className="mkt-difference__title">
              Built from scratch &mdash; and built to fit{' '}
              <span className="text-gradient">your business.</span>
            </h2>
            <p className="mkt-difference__lede">
              Real engineering experience from some of the biggest companies in
              the world &mdash; applied to your business, without the
              big-consultancy price tag.
            </p>
          </div>

          <ol className="mkt-difference__list">
            {/* 01 — WordPress */}
            <li className="mkt-principle">
              <span className="mkt-principle__num" aria-hidden="true">01</span>
              <div className="mkt-principle__body">
                <h3 className="mkt-principle__title">
                  We do not use <span className="mkt-principle__strike">WordPress</span>.
                </h3>
                <ul className="mkt-bullets">
                  <li>
                    <BulletIcon name="plug" />
                    <div>
                      <strong>Plugin bloat by design.</strong> The whole
                      ecosystem nudges you into buying plugins for everything
                      &mdash; every feature is another vendor, another
                      dependency, and another point of failure that slows
                      your site down.
                    </div>
                  </li>
                  <li>
                    <BulletIcon name="cpu" />
                    <div>
                      <strong>An aging tech stack.</strong> PHP and MySQL are
                      showing their age. Single-threaded request handling,
                      slow renders, and scaling that costs real money. The
                      modern web simply isn&rsquo;t built this way any more.
                    </div>
                  </li>
                  <li>
                    <BulletIcon name="shield" />
                    <div>
                      <strong>A constant security target.</strong> Powering
                      around 40% of the web makes WordPress the biggest
                      attack surface online &mdash; new vulnerabilities land
                      every week across core, themes and plugins. That&rsquo;s
                      a problem you inherit by default.
                    </div>
                  </li>
                  <li>
                    <BulletIcon name="brush" />
                    <div>
                      <strong>Themes lock you in just as hard.</strong> Want
                      a real custom layout? You&rsquo;re overriding your
                      theme, child-theming everything, and hoping the next
                      update doesn&rsquo;t quietly undo your work.
                    </div>
                  </li>
                </ul>
              </div>
            </li>

            {/* 02 — Website builders */}
            <li className="mkt-principle">
              <span className="mkt-principle__num" aria-hidden="true">02</span>
              <div className="mkt-principle__body">
                <h3 className="mkt-principle__title">
                  We do not use{' '}
                  <span className="mkt-principle__strike">Squarespace</span>,{' '}
                  <span className="mkt-principle__strike">Wix</span> or any
                  other website builder.
                </h3>
                <ul className="mkt-bullets">
                  <li>
                    <BulletIcon name="template" />
                    <div>
                      <strong>Pre-baked templates, not your business.</strong>
                      Builders are designed for the average site. Yours
                      isn&rsquo;t average &mdash; we tailor everything to
                      your exact specification.
                    </div>
                  </li>
                  <li>
                    <BulletIcon name="brand" />
                    <div>
                      <strong>Branding from scratch, applied everywhere.</strong>
                      We design your brand &mdash; logo, palette, type, voice
                      &mdash; and apply it consistently across the website,
                      documents, proposals, social and email. Not squeezed
                      into a template&rsquo;s layout.
                    </div>
                  </li>
                  <li>
                    <BulletIcon name="box" />
                    <div>
                      <strong>Limited the moment you grow.</strong> The day
                      you need an integration, a custom flow or a real
                      e-commerce edge case &mdash; the builder hits a wall.
                    </div>
                  </li>
                  <li>
                    <BulletIcon name="chain" />
                    <div>
                      <strong>Locked into their ecosystem.</strong> Your
                      content, your domain handling, your data &mdash; all
                      tied to their platform, on their terms, at their price.
                    </div>
                  </li>
                </ul>
              </div>
            </li>

            {/* Interstitial — what we DO use */}
            <li className="mkt-flip" aria-label="What we build with instead">
              <div className="mkt-flip__inner">
                <span className="mkt-flip__eyebrow">Instead, we build with</span>
                <h3 className="mkt-flip__title">
                  Modern frameworks like{' '}
                  <span className="mkt-flip__tech">Next.js</span> and{' '}
                  <span className="mkt-flip__tech">React</span>.
                </h3>
                <p className="mkt-flip__sub">
                  A custom front end where every pixel is aligned to{' '}
                  <em>your</em> needs &mdash; the kind of craft that makes
                  your website stand out.
                </p>
                <ul className="mkt-flip__pills">
                  {[
                    'Next.js',
                    'React',
                    'TypeScript',
                    'Tailwind CSS',
                    'Node.js',
                    'Headless CMS',
                    'Edge hosting',
                  ].map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </div>
            </li>

            {/* 03 — We know both sides (the unicorn point) */}
            <li className="mkt-principle">
              <span className="mkt-principle__num" aria-hidden="true">03</span>
              <div className="mkt-principle__body">
                <h3 className="mkt-principle__title">
                  Most agencies know one side. We know both.
                </h3>
                <p>
                  Most marketing agencies hire marketing people, from
                  marketing backgrounds, who&rsquo;ve only ever worked at
                  marketing firms. They commission designs they
                  can&rsquo;t build, pitch ideas without understanding the
                  technology, and quietly hand the engineering off to a
                  vendor who doesn&rsquo;t speak the same language.
                  They don&rsquo;t know UX, usability, accessibility, or
                  what makes a website actually fast &mdash; because
                  they&rsquo;ve never built one.
                </p>
                <p>
                  We&rsquo;re different. Over a decade building software
                  for some of the biggest companies in the world &mdash;
                  working <em>alongside</em> their marketing, brand and
                  growth teams the whole time. We understand UX,
                  accessibility, performance and modern web technology
                  because we <strong>build</strong> it. We understand
                  marketing &mdash; voice, conversion, growth, search,
                  paid media &mdash; because we&rsquo;ve <strong>done</strong>{' '}
                  it.
                </p>
                <p>
                  One team that can have the marketing conversation and
                  the engineering conversation in the same meeting. No
                  translation tax. No &ldquo;that&rsquo;s not really
                  possible&rdquo; from a vendor you&rsquo;ve never met.
                  Just both sides, under one roof.
                </p>
              </div>
            </li>

            {/* 04 — Personal support */}
            <li className="mkt-principle">
              <span className="mkt-principle__num" aria-hidden="true">04</span>
              <div className="mkt-principle__body">
                <h3 className="mkt-principle__title">
                  We&rsquo;re always here. On a plan that fits you.
                </h3>
                <p>
                  After launch, we don&rsquo;t disappear. Our support is
                  personal &mdash; real engineers, real attention, real
                  continuity from the people who built your site.
                </p>
                <p>
                  We offer tiered support packages sized to your business and
                  your needs &mdash; from light-touch peace-of-mind, through
                  regular hands-on improvements, to a deeply embedded ongoing
                  partnership. And because business needs change, your
                  package changes with you. Move up, move down, pause,
                  restart &mdash; no lock-ins, no upsell pressure. Just real
                  support, from real people, when you need it.
                </p>
              </div>
            </li>

            {/* 05 — Big-company experience */}
            <li className="mkt-principle">
              <span className="mkt-principle__num" aria-hidden="true">05</span>
              <div className="mkt-principle__body">
                <h3 className="mkt-principle__title">
                  Big-company experience. Small-business price.
                </h3>
                <p>
                  Over a decade of full-stack development experience working at
                  some of the biggest companies in the world. The same
                  engineering rigour those companies pay top-tier consultancies
                  thousands a day &mdash; sometimes millions a project &mdash;
                  to deliver, applied to your business at a fraction of the
                  cost. That&rsquo;s the difference: real experience from real
                  big-company work, transferred straight to you.
                </p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      {/* Google-ready trust strip */}
      <section className="mkt-google-strip" aria-label="Google-ready setups">
        <div className="mkt-google-strip__inner">
          <div className="mkt-google-strip__lead">
            <GoogleG className="mkt-g mkt-g--lg" />
            <div>
              <span className="mkt-google-strip__eyebrow">Google-ready</span>
              <p className="mkt-google-strip__title">
                We build, configure and run the Google stack your business actually needs.
              </p>
            </div>
          </div>
          <ul className="mkt-google-strip__list">
            <li><GoogleG /> Analytics 4</li>
            <li><GoogleG /> Tag Manager</li>
            <li><GoogleG /> Search Console</li>
            <li><GoogleG /> Business Profile</li>
            <li><GoogleG /> Ads</li>
          </ul>
          <p className="mkt-google-strip__note">
            Google, the Google logo, Google Analytics, Google Ads, Google Tag Manager,
            Google Search Console and Google Business Profile are trademarks of Google LLC.
            QA Tech is an independent provider; logos are shown to indicate the products we set up
            for clients.
          </p>
        </div>
      </section>

      {/* Services grid */}
      <section className="mkt-services" id="mkt-services">
        <div className="mkt-services__header">
          <h2>Marketing services that move the needle</h2>
          <p>
            Each service is delivered as a clean, well-documented setup &mdash; no
            black-box dashboards or leftover spaghetti tracking.
          </p>
        </div>
        <div className="mkt-services__grid">
          {services.map((s) => {
            const inner = (
              <>
                {s.google && (
                  <span className="mkt-card__google" aria-label="Google-ready">
                    <GoogleG />
                    <span>Google-ready</span>
                  </span>
                )}
                <div className="mkt-card__icon" aria-hidden="true">
                  <MktIcon name={s.icon} />
                </div>
                <h3 className="mkt-card__title">{s.title}</h3>
                <p className="mkt-card__body">{s.body}</p>
                <ul className="mkt-card__list">
                  {s.points.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
                {s.to && (
                  <span className="mkt-card__more">
                    Learn more <span aria-hidden="true">&rarr;</span>
                  </span>
                )}
              </>
            )

            const className = `mkt-card mkt-card--${s.icon}${s.to ? ' mkt-card--link' : ''}`

            return s.to ? (
              <Link
                key={s.title}
                to={s.to}
                className={className}
                onClick={() => trackCtaClick(s.title, 'marketing_services_grid')}
              >
                {inner}
              </Link>
            ) : (
              <article key={s.title} className={className}>
                {inner}
              </article>
            )
          })}
        </div>
      </section>

      {/* Why us */}
      <section className="mkt-why">
        <div className="mkt-why__inner">
          <h2>Why work with QA Tech</h2>
          <div className="mkt-why__grid">
            {reasons.map((r) => (
              <div key={r.title} className="mkt-why__item">
                <h4>{r.title}</h4>
                <p>{r.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="mkt-process">
        <div className="mkt-process__header">
          <h2>How we work</h2>
          <p>A simple, predictable engagement &mdash; from first call to ongoing growth.</p>
        </div>
        <ol className="mkt-process__list">
          <li><span>01</span><h4>Discover</h4><p>We learn your business, audience and goals &mdash; what success looks like.</p></li>
          <li><span>02</span><h4>Plan</h4><p>A clear roadmap with priorities, timelines and what we&rsquo;ll measure.</p></li>
          <li><span>03</span><h4>Build</h4><p>Site, tracking, ads and brand assets are built, tested and shipped.</p></li>
          <li><span>04</span><h4>Grow</h4><p>We optimise, report and iterate &mdash; turning data into more customers.</p></li>
        </ol>
      </section>

      {/* CTA + contact */}
      <section className="mkt-cta">
        <div className="mkt-cta__inner">
          <h2>Ready to grow?</h2>
          <p>
            Tell us a bit about your business and we&rsquo;ll come back with a clear,
            no-obligation plan.
          </p>
          <Link to="/" className="hero-secondary-link">&larr; Back to home</Link>
        </div>
      </section>

      <div id="mkt-contact">
        <ContactForm />
      </div>
    </div>
  </>
)

const MktIcon = ({ name }) => {
  switch (name) {
    case 'web':
      return (
        <svg viewBox="0 0 24 24" fill="none">
          <rect x="3" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
          <path d="M3 8h18" stroke="currentColor" strokeWidth="1.8" />
          <circle cx="6" cy="6" r="0.7" fill="currentColor" />
          <circle cx="8" cy="6" r="0.7" fill="currentColor" />
          <circle cx="10" cy="6" r="0.7" fill="currentColor" />
        </svg>
      )
    case 'seo':
      return (
        <svg viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.8" />
          <path d="M16 16l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M9 11h4M11 9v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      )
    case 'analytics':
      return (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M4 20V10M10 20V4M16 20v-6M22 20H2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      )
    case 'events':
      return (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M12 3v6M12 15v6M3 12h6M15 12h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      )
    case 'search':
      return (
        <svg viewBox="0 0 24 24" fill="none">
          <circle cx="10" cy="10" r="6" stroke="currentColor" strokeWidth="1.8" />
          <path d="M15 15l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      )
    case 'profile':
      return (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M12 21s-7-5.5-7-11a7 7 0 1114 0c0 5.5-7 11-7 11z" stroke="currentColor" strokeWidth="1.8" />
          <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      )
    case 'ads':
      return (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M3 10v4a2 2 0 002 2h2l5 4V4L7 8H5a2 2 0 00-2 2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M16 9a4 4 0 010 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M19 6a8 8 0 010 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      )
    case 'social':
      return (
        <svg viewBox="0 0 24 24" fill="none">
          {/* heart over speech bubble — social engagement mark */}
          <path d="M4 5h12a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3h-3l-3 3v-3H4a3 3 0 0 1-3-3 0 3 0 0 1 0-3V8a3 3 0 0 1 3-3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M7 10a2 2 0 0 1 3-1.7A2 2 0 0 1 13 10c0 2-3 4-3 4s-3-2-3-4z" fill="currentColor" stroke="none" />
        </svg>
      )
    case 'rebrand':
      return (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M4 21l4-1 11-11-3-3L5 17l-1 4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M14 6l3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      )
    default:
      return null
  }
}

export default Marketing
